import { useCallback, useEffect, useRef, useState, useContext } from 'react';

import type { Provider, CollabEditProvider } from '@atlaskit/collab-provider';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { setUserAgent, friendlyName, version } from '@confluence/browser-helper';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { EDIT_PAGE_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useCollabNetworkStatusActions } from '@confluence/collab-network-status';

/**
 * Listens to collab provider disconnected event for unexpected disconnections of the socket. If the socket was closed
 * without an explicit call from the client or server to .disconnect(), then this indicates a network dropout.
 */
export const useDisconnected = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	contentId: string,
	contentType: string,
): { internetLost: boolean; disconnected: boolean } => {
	// we have been disconnected for confluence_frontend_ncs_disconnected_delay_ms time,
	// assume internet is lost which will result in disabling editor
	const [internetLost, setInternetLost] = useState<boolean>(false);
	const [disconnected, setDisconnected] = useState<boolean>(false);
	const networkStatusActions = useCollabNetworkStatusActions();

	const experienceTracker = useContext(ExperienceTrackerContext);

	// disconnected ref holds the latest socket disconnected status
	const disconnectRef = useRef<boolean>(false);
	// data for analytics
	const collabSessionIdRef = useRef<string>();
	const lastDisconnectedTime = useRef<number>();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// wait period to allow socket.io (collab provider) to try to reconnect once before we declare internet as lost
	// socket.io has a default reconnection delay which is random time between a min and max, we need to wait at least as the max reconnection delay
	// https://socket.io/docs/v3/client-initialization/#reconnectionDelay
	const DISCONNECTED_DELAY_MS: number = Number(
		StatsigConfigurations.getNumber('confluence_frontend_ncs_disconnected_delay_ms', 0),
	); // default to 0 if FF not set, meaning we don't wait to confirm disconnection

	const triggerAnalyticsEvent = useCallback(
		// prettier-ignore
		({ trigger, attributes, }: { trigger: "disconnected" | "reconnected"; attributes: any; }) => {
      createAnalyticsEvent({
        type: "sendOperationalEvent", // for performance
        data: {
          action: trigger,
          actionSubject: "nativeEditor",
          source: "confluenceFrontend",
          objectId: contentId,
          objectType: contentType,
          attributes,
        },
      }).fire();
    },
		[createAnalyticsEvent, contentType, contentId],
	);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleDisconnected = ({ reason }: { reason: string }) => {
			if (reason === 'SOCKET_CLOSED' || reason === 'SOCKET_ERROR') {
				triggerAnalyticsEvent({
					trigger: 'disconnected',
					attributes: {
						collabSessionId: collabSessionIdRef.current,
						reason,
						time: Date.now(),
						online: navigator.onLine,
					},
				});
				disconnectRef.current = true;
				lastDisconnectedTime.current = Date.now(); // used for analytics

				// if disconnected delay is 0, then we don't do anything with the timeout and immediately report internet lost
				if (DISCONNECTED_DELAY_MS == 0) {
					setInternetLost(true);
					networkStatusActions.setIsCollabOffline(true);
					return;
				}

				// else disconnected delay is set, so wait until confirming internet is lost
				setTimeout(() => {
					if (disconnectRef.current) {
						// still disconnected after wait period, persist it and notify hook caller
						setInternetLost(true);
						networkStatusActions.setIsCollabOffline(true);
					}
				}, DISCONNECTED_DELAY_MS);
			} else {
				setDisconnected(true);
			}
			experienceTracker.attachDebugPoint({
				name: EDIT_PAGE_LOAD_EXPERIENCE,
				message: 'NCS Disconnected',
				attributes: { reason },
			});
		};

		const handleReconnected = ({ sid }) => {
			if (disconnectRef.current) {
				// only fire reconnected event when we have already disconnected
				setUserAgent();
				triggerAnalyticsEvent({
					trigger: 'reconnected',
					attributes: {
						collabSessionId: collabSessionIdRef.current, // pass in previous sid to correctly match reconnect to previous disconnect
						disconnectedTimeMs: Date.now() - lastDisconnectedTime.current!,
						time: Date.now(),
						browser: friendlyName(),
						browserVersion: version(),
						online: navigator.onLine,
					},
				});
				experienceTracker.attachDebugPoint({
					name: EDIT_PAGE_LOAD_EXPERIENCE,
					message: 'NCS Reconnected',
				});
			}
			collabSessionIdRef.current = sid;
			disconnectRef.current = false;
			setInternetLost(false);
			setDisconnected(false);
			networkStatusActions.setIsCollabOffline(false);
		};

		collabEditProvider.on('disconnected', handleDisconnected);
		collabEditProvider.on('connected', handleReconnected);

		return () => {
			collabEditProvider.off('disconnected', handleDisconnected);
			collabEditProvider.off('connected', handleReconnected);
		};
	}, [
		collabEditProvider,
		triggerAnalyticsEvent,
		DISCONNECTED_DELAY_MS,
		experienceTracker,
		networkStatusActions,
	]);

	return { internetLost, disconnected };
};
